import {Injectable} from '@angular/core';
import {ConnectorService} from './connector.service';
import {Observable, Subject} from 'rxjs';
import {
    ColonsModel,
    NavigationTableRequestModel,
    NavigationTableResponseItemModel,
    NavigationTableResponseModel,
    PaginationModel,
    PermissionsDocsModel
} from '../../models/file-table.model';
import {
    FileModel,
    IApproverResModel,
    FolderTree,
    RegistryFileModel,
    SearchElement,
    SignersOrVisorsModel,
    VersionsModel,
    IEditFileSigners
} from '../../models/file.model';
import {MemberService} from '../member.service';
import {HttpClient} from '@angular/common/http';
import {AlertService} from '../notify/alert.service';
import {Router} from '@angular/router';

export class FileDataResponse {
    constructor(
        public document: FileModel,
        public right: {
            is_read_all: boolean,
            is_read_approved_doc: boolean,
            is_edit_signer: boolean,
            is_edit_approver: boolean,
            is_edit_settings: boolean,
            is_edit_property: boolean,
            is_create_folder: boolean,
            is_upload_file: boolean,
            is_locked_collection: boolean,
            is_delete: boolean,
            is_read_my_doc: boolean,
            use_custom_rights: boolean,
        },
        public signers: SignersOrVisorsModel[],
       ) {
    }
}

@Injectable({
    providedIn: 'root'
})
export class DocumentsApiService extends ConnectorService {
    private cacheGetFileData: { [id: string]: Subject<FileDataResponse> } = {};

    constructor(
        private memberService: MemberService,
        protected http: HttpClient,
        protected alert: AlertService,
        protected router: Router,
    ) {
        super(http, alert, router);
    }

    /**
     * @param request
     */
    getFoldersAndFilesTable(request: NavigationTableRequestModel): Observable<NavigationTableResponseModel> {
        return this.get('instance/edi/folders/table', request);
    }

    /**
     * коллекция по ID
     * @param request
     */
    getCollectionTable(request: NavigationTableRequestModel): Observable<NavigationTableResponseModel> {
        return this.get('instance/edi/collections/' + request.id, request);
    }

    /**
     * Данные о файле по его ukey
     * @param ukey
     */
    getFileData(ukey: string, domain?:string): Subject<FileDataResponse> {
        if (!this.cacheGetFileData[ukey]) {
            this.cacheGetFileData[ukey] = new Subject();
            this.get('instance/edi/files/' + ukey,undefined,domain).subscribe(res => {
                const signersGuids: string[] = res.signers.map(signer => {
                    return signer.member_guid;
                });
                const ownerVersionsGuid: string[] = res.document.versions.map((version: VersionsModel) => {
                    return version.guid_owner;
                });

                this.memberService.memberGuidsHandler(signersGuids.concat(ownerVersionsGuid)).then(members => {
                    res.signers = res.signers.map(signer => {
                        signer.user = members.find(member => member.guid === signer.member_guid);
                        return signer;
                    });
                    res.document.signers = res.signers;

                    res.document.versions = res.document.versions.map(version => {
                        version.owner = members.find(member => member.guid === version.guid_owner);
                        return version;
                    });
                    if (res.document.latest_version) {
                        res.document.latest_version.owner = members.find(member => member.guid === res.document.latest_version.guid_owner);
                    }
                    this.cacheGetFileData[ukey].next(res);
                    this.cacheGetFileData[ukey].complete();
                    this.cacheGetFileData[ukey] = null;
                    delete this.cacheGetFileData[ukey];
                });
            });
        }
        return this.cacheGetFileData[ukey];
    }

    /**
     * Создание папки
     * @param data
     */
    createFolder(data): Observable<any> {
        return this.post('instance/edi/folders', data);
    }

    /**
     * Загрузка нового файла
     * @param data
     */
    createFile(data): Observable<{
        group: FileModel,
        message: string,
        task_id: string,
        version: VersionsModel
    }> {
        const form = new FormData();
        for (const dataKey of Object.keys(data)) {
            if (dataKey === 'document') {
                for (const file of data[dataKey]) {
                    form.append(dataKey, file);
                }
            } else {
                form.append(dataKey, data[dataKey]);
            }
        }
        return this.post('instance/edi/files', form, false);
    }

    /**
     * Обновление версии файла
     * @param file
     * @param data
     */
    updateFile(file: FileModel, data: object): Observable<any> {
        const form = new FormData();
        for (const dataKey of Object.keys(data)) {
            form.append(dataKey, data[dataKey]);
        }
        return this.put('instance/edi/files/' + file.key, form, false);
    }

    /**
     * Добавление нового файла в коллекцию
     * @param data
     * @param id
     */
    uploadFileInCollection(data, id): Observable<any> {
        const form = new FormData();
        for (const dataKey of Object.keys(data)) {
            if (dataKey === 'document') {
                for (const file of data[dataKey]) {
                    form.append(dataKey, file);
                }
            } else {
                form.append(dataKey, data[dataKey]);
            }
        }
        return this.post('instance/edi/collections/' + id, form, false);
    }

    /**
     * Получение тела документа по его UKey PDF / DOCX
     * @param UKey
     * @param docFormat
     * @param isOrigin
     * @param isDiff
     */
    getFileBodyVersion(UKey: string, docFormat: 'docx' | 'pdf' | 'xlsx', isOrigin: boolean, isDiff = false): Observable<any> {
        const data = {
            diff: isDiff,
            format: docFormat,
            original: isOrigin
        };
        return this.getBodyFile('instance/edi/versions/' + UKey, data);
    }

    /**
     * Получение тела pdf версии
     * @param ukey
     */
    getIntermediateVersion(ukey): Observable<any> {
        return this.getBodyFile('instance/edi/pdf/' + ukey);
    }

    /**
     * Получение ZIP архива коллекции
     * @param collectionID
     * @param isDiff
     */
    getZipCollection(collectionID, isDiff = false): Observable<any> {
        const data = {
            diff: isDiff,
            format: 'zip'
        };
        return this.getBodyFile('instance/edi/collections/' + collectionID + '/zip', data);
    }

    /**
     * Подписание документа
     * @param pinCode пин код пользователя
     * @param fileKey Ключ файла
     */
    signFile(pinCode, fileKey): Observable<any> {
        return this.put('instance/edi/files/sign/' + fileKey, {PIN_code: pinCode});
    }

    /**
     * файлы из "локального" реестра
     */
    getRegistry(pagination: PaginationModel): Observable<{
        current_page: number,
        page_count: number,
        data: RegistryFileModel[]
    }> {
        return this.get('instance/edi/files/registry/', pagination);
    }

    /**
     * ПОиск по файлам
     * @param query
     * Текст
     */
    search(query): Observable<{
        message: string,
        results: SearchElement[]
    }> {
        return this.get('instance/edi/search/', query);
    }

    /**
     * Удаление файла и его версий
     * @param fvkey - ключ группы файлов
     * @returns
     */
    deleteFile(fvkey): Observable<any> {
        return this.delete('instance/edi/files/' + fvkey);
    }

    /**
     * Удаление коллекции (частичное удаление, если в коллекции есть подписанные документы)
     * @param collectionId
     * @returns
     */
    deleteCollection(collectionId): Observable<any> {
        return this.delete('instance/edi/collections/' + collectionId);
    }

    /**
     * Удаление папки
     * @param idx - id папки
     * @returns
     */
    deleteFolder(idx): Observable<any> {
        return this.delete('instance/edi/folders/' + idx);
    }

    /**
     * Добавить подписанта в документ
     * @param fvkey
     * @param memberGuid
     */
    addSigner(fvkey, memberGuid): Observable<IEditFileSigners> {
        return this.post('instance/edi/files/' + fvkey + '/signers/' + memberGuid);
    }

    /**
     * Удалить подписанта из документа
     * @param fvkey
     * @param memberGuid
     */
    deleteSigner(fvkey, memberGuid): Observable<IEditFileSigners> {
        return this.delete('instance/edi/files/' + fvkey + '/signers/' + memberGuid);
    }

    /**
     * Изменение папки
     * @param data
     */
    editFolder(data): Observable<{ message: string, folder: NavigationTableResponseItemModel }> {
        return this.put('instance/edi/folders/' + data.id, data);
    }

    /**
     * Обновить значение проперти
     * @param target
     * @param targetKey
     * @param propertyId
     * @param value
     */
    setOrUpdateProperty(target: 'folder' | 'collection' | 'file',
                        targetKey: string | number,
                        propertyId: number,
                        value: { value: any }): Observable<any> {
        return this.put('instance/edi/properties/' + target + '/' + targetKey + '/' + propertyId, value);
    }

    /**
     * Получение текущих прав папки/коллекции/файла
     * @param rightType
     * @param rightKey
     */
    getDocAccess(rightType: 'file' | 'collection' | 'folder', rightKey: string | number): Observable<{
        structure: ColonsModel[],
        members: PermissionsDocsModel[],
        divisions: PermissionsDocsModel[],
        departments: PermissionsDocsModel[]
    }> {
        return this.get('instance/edi/right/' + rightType + '/' + rightKey);
    }

    /**
     * Установка прав на папку/коллекцию/файл
     * @param rightType
     * @param rightKey
     * @param data
     */
    setDocAccess(rightType: 'file' | 'collection' | 'folder',
                 rightKey: string | number,
                 data: {
                     role_type: 'member' | 'department' | 'division',
                     role_key: string | number,
                     is_read_all: string | 'succession' | 'allowed' | 'denied';
                     is_read_approved_doc: string | 'succession' | 'allowed' | 'denied';
                     is_edit_signer: string | 'succession' | 'allowed' | 'denied';
                     is_edit_approver: string | 'succession' | 'allowed' | 'denied';
                     is_edit_settings: string | 'succession' | 'allowed' | 'denied';
                     is_edit_property: string | 'succession' | 'allowed' | 'denied';
                     is_create_folder: string | 'succession' | 'allowed' | 'denied';
                     is_upload_file: string | 'succession' | 'allowed' | 'denied';
                     is_locked_collection: string | 'succession' | 'allowed' | 'denied';
                     is_delete: string | 'succession' | 'allowed' | 'denied';
                 }
    ): Observable<any> {
        return this.put('instance/edi/right/' + rightType + '/' + rightKey, data);
    }

    /**
     * Удаление прав папки/коллекции/файла
     * @param rightType
     * @param rightKey
     * @param data
     */
    deleteDocAccess(rightType: 'file' | 'collection' | 'folder',
                    rightKey: string | number,
                    data: {
                        role_type: 'member' | 'departament' | 'division',
                        role_key: string | number,
                    }
    ): Observable<any> {
        return this.delete('instance/edi/right/' + rightType + '/' + rightKey, data);
    }

    /**
     * Получение визоров файла
     * @param fileKey
     */
    getFileVisors(fileKey: string): Observable<{ approvers: SignersOrVisorsModel[] }> {
        return this.get('instance/edi/files/' + fileKey + '/approvers');
    }

    /**
     * Назначить визора для файла
     * @param fileKey
     * @param memberGuid
     * @param data
     */
    addVisorToFile(fileKey: string, memberGuid: string, data: { type_approve: 'any' | 'all'|null }): Observable<any> {
        return this.post('instance/edi/files/' + fileKey + '/approvers/' + memberGuid, data);
    }

    /**
     * Назначить визора для коллекции
     * @param collectionId
     * @param memberGuid
     * @param data
     */
    addVisorToCollection(collectionId: number, memberGuid: string, data: {
        type_approve_default: 'any' | 'all'| null
    }): Observable<any> {
        return this.post('instance/edi/collections/' + collectionId + '/approvers/' + memberGuid, data);
    }

    /**
     * Назначить группу визоров для файла (отдел)
     * @param data
     */
    addVisorGroupToFile(
        data: {
            type_obj: 'file' | 'collection',
            key_obj: string | number,
            division_id: number,
            type_approve_default: 'any' | 'all' | null
        }
    ): Observable<any> {
        return this.post('instance/edi/files/approvers/multi/division', data);
    }

    /**
     * Удалить визора из файла
     * @param fileKey
     * @param memberGuid
     */
    deleteVisorFromFile(fileKey: string, memberGuid: string): Observable<any> {
        return this.delete('instance/edi/files/' + fileKey + '/approvers/' + memberGuid);
    }

    /**
     * Поставить визу на файл
     * @param fileKey
     * @param data
     */
    applyDocument(fileKey: string, data: { PIN_code: string | null }): Observable<IApproverResModel> {
        return this.put('instance/edi/files/' + fileKey + '/approved', data);
    }

    /**
     * Обновление названия и описания коллекции
     * @param idCollection
     * @param data
     */
    editCollection(idCollection: number, data: { name: string, collection_description: string }): Observable<any> {
        return this.put('instance/edi/collections/' + idCollection + '/up', data);
    }

    /**
     * Обновление названия и описания файла
     * @param fileKey
     * @param data
     */
    editDocumentData(fileKey: string, data: { name: string, file_description: string }): Observable<any> {
        return this.put('instance/edi/files/' + fileKey + '/up', data);
    }

    /**
     * Get folders tree for member
     * @param guid
     */
    getFoldersTree(guid?: string): Observable<{ tree: FolderTree[] }> {
        return this.get('instance/edi/folders/tree', {guid});
    }

    /**
     * Блокировка коллекции
     * @param collectionId
     * @param isBlock
     */
    lockCollection(collectionId: number, isBlock: boolean): Observable<any> {
        return this.put('instance/edi/collections/' + collectionId + '/block?is_blocked=' + isBlock);
    }
}
